import React, { useCallback, useEffect, useState } from 'react';
import './AdminCostsPage.scss';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Column } from 'primereact/column';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import StatTile from '../../../shared/components/stat-tile/StatTile';
import type {
  AdminCostMonthResponse,
  AdminCostProvider,
  AdminCostUnit,
  AdminUnitPrice,
  AdminUnpricedKey,
} from '@brand24-original/llm-types';
import {
  addCostPrice,
  currentMonth,
  formatUsd,
  getCostMonth,
  getCostPrices,
} from '../../../shared/services/admin/costs/AdminCostsService';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { AdminResource, getAdminPermissions } from '../../../shared/models/admin/permissions/AdminPermission';

const PROVIDERS: AdminCostProvider[] = [
  'openai',
  'anthropic',
  'google',
  'xai',
  'perplexity',
  'openrouter',
  'brightdata',
  'semrush',
  'oxylabs',
  'jina',
  'scrapfly',
  'microsoft',
  'cloro',
];

/** What the admin types vs. what is stored: prices are entered per the vendor's natural lot. */
const UNITS: { unit: AdminCostUnit; label: string; lot: number }[] = [
  { unit: 'input_tokens', label: 'USD per 1M input tokens', lot: 1_000_000 },
  { unit: 'output_tokens', label: 'USD per 1M output tokens', lot: 1_000_000 },
  { unit: 'cached_input_tokens', label: 'USD per 1M cached input tokens', lot: 1_000_000 },
  { unit: 'answer', label: 'USD per 1000 answers', lot: 1000 },
  { unit: 'request', label: 'USD per 1000 requests', lot: 1000 },
  { unit: 'api_unit', label: 'USD per 1000 API units', lot: 1000 },
  { unit: 'credit', label: 'USD per 1000 credits', lot: 1000 },
  { unit: 'web_search', label: 'USD per 1000 web searches', lot: 1000 },
  { unit: 'month', label: 'USD per month (flat fee, booked on the shared account)', lot: 1 },
];
const lotOf = (unit: AdminCostUnit): number => UNITS.find((u) => u.unit === unit)?.lot ?? 1;

const monthOptions = (): string[] => {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    out.push(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1)).toISOString().slice(0, 7));
  }
  return out;
};

const AdminCostsPage: React.FC = () => {
  const { accountResponse } = useAccount();
  const canWrite = getAdminPermissions(accountResponse)[AdminResource.COSTS].write;

  const [month, setMonth] = useState(currentMonth());
  const [monthData, setMonthData] = useState<AdminCostMonthResponse | null>(null);
  const [prices, setPrices] = useState<AdminUnitPrice[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [newProvider, setNewProvider] = useState<AdminCostProvider>('openai');
  const [newModel, setNewModel] = useState('');
  const [newUnit, setNewUnit] = useState<AdminCostUnit>('input_tokens');
  const [newPrice, setNewPrice] = useState('');
  const [newValidFrom, setNewValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [addVisible, setAddVisible] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([getCostMonth(month), getCostPrices()]);
      setMonthData(m);
      setPrices(p);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Failed to load costs');
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  /** Opens the add dialog, prefilled from an unpriced key when one is passed ("Price it"). */
  const openAdd = (key?: AdminUnpricedKey) => {
    if (key) {
      setNewProvider(key.provider);
      setNewModel(key.modelOrType);
      setNewUnit(key.unit);
    }
    setNewPrice('');
    setAddError(null);
    setAddVisible(true);
  };

  const handleAdd = async () => {
    const perLot = Number(newPrice.trim().replace(/^\$/, '').replace(',', '.'));
    if (!newModel.trim() || !Number.isFinite(perLot) || perLot < 0) {
      setAddError('Model/type and a non-negative price are required');
      return;
    }
    try {
      await addCostPrice({
        provider: newProvider,
        modelOrType: newModel.trim(),
        unit: newUnit,
        priceUsdPerUnit: perLot / lotOf(newUnit),
        validFrom: newValidFrom.trim(),
      });
      setAddVisible(false);
      await load();
    } catch (e: any) {
      setAddError(e?.response?.data?.error ?? 'Failed to add price');
    }
  };

  return (
    <div className="admin-costs">
      <div className="admin-costs__toolbar">
        <Dropdown value={month} options={monthOptions()} onChange={(e) => setMonth(e.value)} />
      </div>
      {error && <div className="p-error">{error}</div>}

      {monthData && (
        <>
          <div className="stat-tiles">
            <StatTile title="Total this month" value={formatUsd(monthData.totalUsd)} icon="ico--card" />
            <StatTile title="Shared / unattributed" value={formatUsd(monthData.sharedUsd)} icon="ico--globe" />
            <StatTile
              title="Keys without a price"
              value={monthData.unpricedKeys.length}
              icon="ico--warning"
              trend={monthData.unpricedKeys.length > 0 ? 'falling' : 'neutral'}
            />
          </div>

          <div className="admin-costs__grid">
            <div className="admin-costs__section">
              <div className="page-box__title">By provider</div>
              <DataTable value={monthData.byProvider} tableClassName="table" className="table-wrapper" stripedRows emptyMessage={() => <EmptyPanel />}>
                <Column field="key" header="Provider" headerClassName="table__header" />
                <Column header="Quantity" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" body={(r) => r.quantity.toLocaleString()} />
                <Column header="Cost" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" body={(r) => formatUsd(r.costUsd)} />
              </DataTable>
            </div>
            <div className="admin-costs__section">
              <div className="page-box__title">By feature</div>
              <DataTable value={monthData.byFeature} tableClassName="table" className="table-wrapper" stripedRows emptyMessage={() => <EmptyPanel />}>
                <Column field="key" header="Feature" headerClassName="table__header" />
                <Column header="Cost" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" body={(r) => formatUsd(r.costUsd)} />
              </DataTable>
            </div>
          </div>

          <div className="admin-costs__section">
            <div className="page-box__title">Top accounts</div>
            <DataTable value={monthData.topAccounts} tableClassName="table" className="table-wrapper" stripedRows emptyMessage={() => <EmptyPanel />}>
              <Column field="accountId" header="Account" headerClassName="table__header" style={{ width: '100px' }} />
              <Column header="Email" headerClassName="table__header" body={(r) => r.email ? <a href={`/admin/accounts/${r.accountId}`}>{r.email}</a> : '-'} />
              <Column header="Cost" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" body={(r) => formatUsd(r.costUsd)} />
              {month === currentMonth() && (
                <Column header="Projected full month" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" body={(r) => formatUsd(r.projectedUsd)} />
              )}
            </DataTable>
          </div>

          {monthData.unpricedKeys.length > 0 && (
            <div className="admin-costs__section">
              <div className="page-box__title">Metered but without a price</div>
              <DataTable value={monthData.unpricedKeys} tableClassName="table" className="table-wrapper" stripedRows>
                <Column field="provider" header="Provider" headerClassName="table__header" />
                <Column field="modelOrType" header="Model / type" headerClassName="table__header" />
                <Column field="unit" header="Unit" headerClassName="table__header" />
                <Column header="Quantity" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" body={(r) => r.quantity.toLocaleString()} />
                {canWrite && (
                  <Column header="" bodyClassName="table__cell--center" body={(r: AdminUnpricedKey) => <Button className="btn btn--sm" label="Price it" onClick={() => openAdd(r)} />} />
                )}
              </DataTable>
            </div>
          )}
        </>
      )}

      <div className="admin-costs__section">
        <div className="page-box__title">
          Unit prices
          {canWrite && (
            <button type="button" className="btn btn--sm" onClick={() => openAdd()}>
              <span className="ico ico--plus ico--xxs" aria-hidden="true" />
              Add price
            </button>
          )}
        </div>
        <DataTable value={prices} tableClassName="table" className="table-wrapper" stripedRows emptyMessage={() => <EmptyPanel />}>
          <Column field="provider" header="Provider" headerClassName="table__header" />
          <Column field="modelOrType" header="Model / type" headerClassName="table__header" />
          <Column field="unit" header="Unit" headerClassName="table__header" />
          <Column header="Price" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" body={(r: AdminUnitPrice) => `$${(r.priceUsdPerUnit * lotOf(r.unit)).toFixed(4)} / ${lotOf(r.unit).toLocaleString()}`} />
          <Column field="validFrom" header="Valid from" headerClassName="table__header table__header--center" bodyClassName="table__cell--center" />
        </DataTable>
      </div>

      <Dialog
        header="Add unit price"
        visible={addVisible}
        onHide={() => setAddVisible(false)}
        className="dialog-box admin-costs__dialog"
        footer={
          <div className="admin-costs__dialog-footer">
            <button type="button" className="btn btn--sm btn--secondary" onClick={() => setAddVisible(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn--sm" onClick={() => void handleAdd()}>
              Add
            </button>
          </div>
        }
      >
        <div className="admin-costs__form">
          <label className="admin-costs__field">
            <span>Provider</span>
            <Dropdown className="dropdown" value={newProvider} options={PROVIDERS} onChange={(e) => setNewProvider(e.value)} />
          </label>
          <label className="admin-costs__field">
            <span>Model or monitor type</span>
            <InputText value={newModel} onChange={(e) => setNewModel(e.target.value)} placeholder="e.g. gpt-5-nano or GPT_SEARCH_BROWSER" />
          </label>
          <label className="admin-costs__field">
            <span>Unit</span>
            <Dropdown className="dropdown" value={newUnit} options={UNITS.map((u) => ({ label: u.label, value: u.unit }))} onChange={(e) => setNewUnit(e.value)} />
          </label>
          <label className="admin-costs__field">
            <span>{UNITS.find((u) => u.unit === newUnit)?.label ?? 'Price'}</span>
            <InputText value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="0.00" />
          </label>
          <label className="admin-costs__field">
            <span>Valid from</span>
            <InputText value={newValidFrom} onChange={(e) => setNewValidFrom(e.target.value)} placeholder="YYYY-MM-DD" />
          </label>
          {addError && <div className="p-error">{addError}</div>}
        </div>
      </Dialog>
    </div>
  );
};

export default AdminCostsPage;
